import React, { FC } from "react";
import { tv } from "tailwind-variants";
import {Icon} from 'bonprix-design-system';

export interface Props {
    readonly?: boolean;
  /** The rating value between 0 and 5 */
  rating: number;
  /** The size of the rating icons */
  size?: "small" | "large";
  /** Callback fired when the rating changes */
  onChange?: (rating: number) => void;
  /** Show vertical guide lines under first and last stars */
  showGuideLines?: boolean;
  /** Labels to show under the guide lines */
  labels?: {
    left: string;
    right: string;
  };
}

const generateClasses = tv({
  base: "inline-flex relative fill-clr-on-bg-high w-min",
  slots: {
    icons: "flex",
    iconsFull: "flex absolute top-0 left-0 overflow-hidden",
    icon: "shrink-0",
  },
  variants: {
    size: {
      small: {
        icon: "w-3 h-3",
      },
      large: {
        icon: "w-9 h-9",
      },
    },
  },
});

const Rating: FC<Props> = ({rating, size = 'small', onChange, readonly = false, showGuideLines = false, labels}) => {
    const {base, icons, iconsFull, icon} = generateClasses({size});
    const positiveRating = rating < 0 ? 0 : rating;
    const widthStyle = (Math.min(positiveRating, 5) / 5) * 100 + '%';

    const handleClick = (newRating: number) => {
        if (onChange && !readonly) {
            onChange(newRating);
        }
    };

    const renderStar = (index: number, variant: 'review' | 'review-full') => (
        <button
            type="button"
            onClick={() => handleClick(index)}
            className={`bg-transparent border-0 p-0 cursor-pointer px-2 ${readonly ? 'pointer-events-none' : ''}`}
            aria-label={`Rate ${index} out of 5`}
        >
            <Icon variant={variant} className={icon()} />
        </button>
    );

    // Calculate position for guide lines based on size
    // Star button has px-2 (0.5rem padding on each side) + icon width (2.25rem for large, 0.75rem for small)
    // Center = padding (0.5rem) + half icon width (1.125rem for large, 0.375rem for small) - 1px outward
    const guideLineHeight = size === 'large' ? 'h-5' : 'h-3';
    const leftPosition = size === 'large' ? 'left-[25px]' : 'left-[0.875rem]'; // 25px for large
    const rightPosition = size === 'large' ? 'right-[25px]' : 'right-[0.875rem]'; // 25px for large

    const ratingElement = (
        <span className={base()} data-bpds="rating">
            <span className={`${icons()} ${iconsFull()}`} style={{width: widthStyle}}>
                {renderStar(1, 'review-full')}
                {renderStar(2, 'review-full')}
                {renderStar(3, 'review-full')}
                {renderStar(4, 'review-full')}
                {renderStar(5, 'review-full')}
            </span>
            <span className={icons()}>
                {renderStar(1, 'review')}
                {renderStar(2, 'review')}
                {renderStar(3, 'review')}
                {renderStar(4, 'review')}
                {renderStar(5, 'review')}
            </span>
            {showGuideLines && (
                <>
                    <div className={`absolute bottom-[-1.5rem] ${leftPosition} w-0.5 ${guideLineHeight} bg-clr-black`}></div>
                    <div className={`absolute bottom-[-1.5rem] ${rightPosition} w-0.5 ${guideLineHeight} bg-clr-black`}></div>
                </>
            )}
        </span>
    );

    // If labels are provided, wrap in a container with labels
    if (labels) {
        return (
            <div className="flex flex-col items-center gap-[1.75rem] relative">
                {ratingElement}
                <div className="relative w-full" style={{ height: '1.5rem' }}>
                    <span className={`absolute ${leftPosition} text-sm text-gray-600 -translate-x-1/2`}>
                        {labels.left}
                    </span>
                    <span className={`absolute ${rightPosition} text-sm text-gray-600 translate-x-1/2`}>
                        {labels.right}
                    </span>
                </div>
            </div>
        );
    }

    return ratingElement;
};

export default Rating;
